import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Home } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { cn } from "@/lib/cn";

// The GMAT app is launched from the Think Big Techies site; "Back to home"
// returns visitors there. Override with VITE_COMPANY_URL at build time.
const COMPANY_HOME = import.meta.env.VITE_COMPANY_URL || "https://thinkbigtechies.com/";

/* Floating painting tiles positioned around the stage */
interface Tile {
  img: string;
  label: string;
  className: string; // position + size
  dur: number;
  delay: number;
  depth?: "far" | "near";
}

const TILES: Tile[] = [
  { img: "/art/art-study.png", label: "Prepare with AI", className: "left-[3%] top-[16%] w-[22vw] max-w-[260px] md:w-[18vw]", dur: 9, delay: 0 },
  { img: "/art/art-mocktest.png", label: "Sit mock exams", className: "right-[4%] top-[12%] w-[24vw] max-w-[300px] md:w-[19vw]", dur: 11, delay: 0.6 },
  { img: "/art/art-success.png", label: "Hit your 700+", className: "left-[6%] bottom-[10%] w-[26vw] max-w-[320px] md:w-[20vw]", dur: 10, delay: 1.1 },
  { img: "/art/art-college.png", label: "Enter top schools", className: "right-[6%] bottom-[9%] w-[24vw] max-w-[300px] md:w-[19vw]", dur: 12, delay: 0.3 },
  { img: "/art/art-professional.png", label: "Lead your field", className: "left-[38%] top-[4%] hidden w-[16vw] max-w-[220px] lg:block", dur: 13, delay: 1.6, depth: "far" },
  { img: "/art/art-maya.png", label: "Meet Maya", className: "right-[34%] bottom-[3%] hidden w-[15vw] max-w-[210px] lg:block", dur: 14, delay: 0.9, depth: "far" },
];

const MESSAGES: { title: string; accent: string; rainbow?: boolean }[] = [
  { title: "Prepare with an AI that knows", accent: "the GMAT" },
  { title: "Rehearse full", accent: "mock exams" },
  { title: "Celebrate your", accent: "700+ score", rainbow: true },
  { title: "Walk into", accent: "great business schools", rainbow: true },
  { title: "Become the", accent: "leader you're meant to be" },
];

function Confetti() {
  const bits = useMemo(
    () =>
      Array.from({ length: 24 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 4,
        dur: 4 + Math.random() * 4,
        hue: Math.floor(Math.random() * 360),
        size: 6 + Math.random() * 8,
      })),
    [],
  );
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {bits.map((b) => (
        <motion.span
          key={b.id}
          className="absolute top-[-6%] block rounded-[2px]"
          style={{ left: `${b.left}%`, width: b.size, height: b.size * 0.5, background: `hsl(${b.hue} 85% 62%)` }}
          initial={{ y: "-8vh", opacity: 0 }}
          animate={{ y: "110vh", rotate: 360, opacity: [0, 1, 1, 0] }}
          transition={{ duration: b.dur, delay: b.delay, repeat: Infinity, ease: "easeIn" }}
        />
      ))}
    </div>
  );
}

/* Vibrant rainbow background — drifting saturated blobs + animated wash */
function RainbowBackground() {
  const blobs = [
    { c: "#e11d48", cls: "left-[-10%] top-[-8%] size-[46vw]", d: 19 },
    { c: "#f59e0b", cls: "right-[-12%] top-[-6%] size-[42vw]", d: 23 },
    { c: "#eab308", cls: "left-[20%] top-[30%] size-[38vw]", d: 26 },
    { c: "#16a34a", cls: "right-[-8%] bottom-[-10%] size-[44vw]", d: 21 },
    { c: "#2563eb", cls: "left-[-10%] bottom-[-12%] size-[46vw]", d: 25 },
    { c: "#7c3aed", cls: "left-[35%] bottom-[10%] size-[40vw]", d: 28 },
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      {/* deep base */}
      <div className="absolute inset-0 bg-[#0a0713]" />
      {/* full-screen living rainbow wash (glows on the dark base) */}
      <motion.div
        className="absolute inset-0 mix-blend-screen"
        style={{
          backgroundImage:
            "linear-gradient(120deg, #d81b60, #f4511e, #fbc02d, #2e7d32, #1e88e5, #6a1b9a, #d81b60)",
          backgroundSize: "300% 300%",
        }}
        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
      />
      {/* drifting saturated blobs add glowing depth */}
      {blobs.map((b, i) => (
        <motion.div
          key={i}
          className={cn("absolute rounded-full mix-blend-screen", b.cls)}
          style={{ background: b.c, opacity: 0.6, filter: "blur(80px)" }}
          animate={{ x: [0, 60, -40, 0], y: [0, -50, 40, 0], scale: [1, 1.2, 0.95, 1] }}
          transition={{ duration: b.d, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
      {/* dark vignette → rich "dark rainbow", not neon */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 90% 80% at 50% 50%, rgba(8,6,15,0.35) 0%, rgba(8,6,15,0.62) 100%)",
        }}
      />
    </div>
  );
}

/* A real cap toss: caps burst upward-outward from a single point, arc, and fall */
function CapToss() {
  const caps = useMemo(
    () =>
      Array.from({ length: 22 }).map((_, i) => {
        // fan across the upper hemisphere (−160°..−20°)
        const angle = (-160 + Math.random() * 140) * (Math.PI / 180);
        const dist = 34 + Math.random() * 40; // travel distance
        return {
          id: i,
          dx: Math.cos(angle) * dist, // vw
          dy: Math.sin(angle) * dist, // vh (negative = up)
          size: 20 + Math.random() * 24,
          rot: -120 + Math.random() * 240,
          delay: Math.random() * 0.35,
          dur: 2.1 + Math.random() * 1.1,
        };
      }),
    [],
  );
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-[5] overflow-hidden">
      {/* origin: bottom-center, like a crowd tossing caps skyward */}
      <div className="absolute left-1/2 top-[86vh]">
        {caps.map((c) => (
          <motion.span
            key={c.id}
            className="absolute block select-none will-change-transform"
            style={{ fontSize: c.size }}
            initial={{ x: 0, y: 0, rotate: 0, opacity: 0 }}
            animate={{
              x: [`0vw`, `${c.dx}vw`],
              // arc: up past the target then a little back down (gravity)
              y: [`0vh`, `${c.dy}vh`, `${c.dy + 12}vh`],
              rotate: [0, c.rot],
              opacity: [0, 1, 1, 0],
            }}
            transition={{
              duration: c.dur,
              delay: c.delay,
              repeat: Infinity,
              repeatDelay: 1.4,
              ease: "easeOut",
              times: [0, 0.7, 1],
            }}
          >
            🎓
          </motion.span>
        ))}
      </div>
    </div>
  );
}

/* Flowing rainbow ribbon streamers that rise and sway */
function Ribbons() {
  const hues = [348, 32, 48, 142, 217, 268];
  const ribbons = useMemo(
    () =>
      Array.from({ length: 14 }).map((_, i) => ({
        id: i,
        startX: 8 + Math.random() * 84, // vw
        rise: 70 + Math.random() * 40, // vh
        sway: 6 + Math.random() * 12,
        w: 4 + Math.random() * 5,
        h: 46 + Math.random() * 70,
        hue: hues[i % hues.length],
        delay: Math.random() * 3,
        dur: 4.5 + Math.random() * 3,
      })),
    [],
  );
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-[4] overflow-hidden">
      {ribbons.map((r) => (
        <motion.span
          key={r.id}
          className="absolute bottom-[-12vh] block rounded-full"
          style={{
            left: `${r.startX}vw`,
            width: r.w,
            height: r.h,
            background: `linear-gradient(180deg, hsl(${r.hue} 90% 62%), hsl(${(r.hue + 40) % 360} 90% 60%))`,
          }}
          initial={{ y: 0, x: 0, opacity: 0 }}
          animate={{
            y: [`0vh`, `-${r.rise}vh`],
            x: [`0px`, `${r.sway}px`, `-${r.sway}px`, `0px`],
            rotate: [-8, 8, -6, 6],
            opacity: [0, 0.9, 0.9, 0],
          }}
          transition={{ duration: r.dur, delay: r.delay, repeat: Infinity, ease: "easeOut" }}
        />
      ))}
    </div>
  );
}

/* Twinkling sparkles for a touch of magic */
function Sparkles() {
  const stars = useMemo(
    () =>
      Array.from({ length: 30 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 6 + Math.random() * 12,
        delay: Math.random() * 4,
        dur: 2 + Math.random() * 3,
      })),
    [],
  );
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-[4] overflow-hidden">
      {stars.map((s) => (
        <motion.span
          key={s.id}
          className="absolute select-none"
          style={{ left: `${s.left}%`, top: `${s.top}%`, fontSize: s.size }}
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{ opacity: [0, 1, 0], scale: [0.4, 1, 0.4] }}
          transition={{ duration: s.dur, delay: s.delay, repeat: Infinity, ease: "easeInOut" }}
        >
          ✨
        </motion.span>
      ))}
    </div>
  );
}

function FloatingTile({ t }: { t: Tile }) {
  return (
    <motion.figure
      className={cn("absolute z-0", t.className)}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{
        opacity: t.depth === "far" ? 0.55 : 0.92,
        scale: 1,
        y: [0, -18, 0],
        x: [0, 8, 0],
        rotate: [-2, 2, -2],
      }}
      transition={{
        opacity: { duration: 1.2, delay: t.delay },
        scale: { duration: 1.2, delay: t.delay },
        y: { duration: t.dur, repeat: Infinity, ease: "easeInOut", delay: t.delay },
        x: { duration: t.dur * 1.3, repeat: Infinity, ease: "easeInOut", delay: t.delay },
        rotate: { duration: t.dur * 1.6, repeat: Infinity, ease: "easeInOut", delay: t.delay },
      }}
    >
      <div
        className={cn(
          "grain overflow-hidden rounded-[20px] border border-border/50 shadow-editorial-lg",
          t.depth === "far" && "blur-[1px]",
        )}
      >
        <img src={t.img} alt={t.label} loading="lazy" className="h-full w-full object-cover" />
      </div>
      <figcaption className="mt-2 text-center text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {t.label}
      </figcaption>
    </motion.figure>
  );
}

export function Landing() {
  const [m, setM] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setM((v) => (v + 1) % MESSAGES.length), 3400);
    return () => clearInterval(t);
  }, []);
  const msg = MESSAGES[m];

  return (
    <div className="dark relative h-screen w-screen overflow-hidden bg-[#0a0713] text-foreground">
      {/* vibrant rainbow background */}
      <RainbowBackground />

      {/* floating paintings */}
      {TILES.map((t) => (
        <FloatingTile key={t.img} t={t} />
      ))}

      {/* celebration layers */}
      <Ribbons />
      <Sparkles />
      <CapToss />
      {msg.rainbow && <Confetti />}

      {/* top bar */}
      <header className="relative z-20">
        <div className="container flex h-16 items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <a href={COMPANY_HOME}>
              <Button variant="ghost" size="sm" className="gap-1.5">
                <Home className="size-4" /> Back to home
              </Button>
            </a>
            <ThemeToggle compact />
            <Link to="/signin">
              <Button variant="ghost" size="sm">Sign in</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* center message + single sign-up */}
      <div className="relative z-10 flex h-[calc(100vh-4rem)] items-center justify-center px-6">
        <div className="glass relative mx-auto max-w-md rounded-[28px] px-6 py-7 text-center shadow-editorial-lg">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 inline-flex items-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground"
          >
            <span className="hero-rule" /> AI GMAT tutor
          </motion.div>

          <div className="min-h-[2.6em]">
            <AnimatePresence mode="wait">
              <motion.h1
                key={m}
                initial={{ opacity: 0, y: 18, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -14, filter: "blur(6px)" }}
                transition={{ duration: 0.55, ease: "easeOut" }}
                className="font-serif text-3xl leading-[1.02] tracking-tight md:text-4xl"
              >
                {msg.title}{" "}
                <span className={cn("italic", msg.rainbow ? "gradient-rainbow" : "text-primary")}>
                  {msg.accent}
                </span>
                .
              </motion.h1>
            </AnimatePresence>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mt-5 flex flex-col items-center gap-2"
          >
            <Link to="/signin">
              <Button variant="default" size="lg" className="shadow-editorial-lg">
                Start free <ArrowRight className="size-4" />
              </Button>
            </Link>
            <span className="text-[11px] text-muted-foreground">
              Google or email · progress saved across devices
            </span>
          </motion.div>

          {/* scene dots */}
          <div className="mt-6 flex items-center justify-center gap-2">
            {MESSAGES.map((_, n) => (
              <button
                key={n}
                aria-label={`Message ${n + 1}`}
                onClick={() => setM(n)}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  n === m ? "w-8 bg-foreground" : "w-1.5 bg-foreground/25 hover:bg-foreground/50",
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
